import { formatQuantity } from "@/lib/format";
import { ACCENT, INK, LINE, MUTED, ZEBRA, loadPdfMake, slug } from "@/lib/pdf";

const stamp = (value) =>
  value
    ? new Date(value).toLocaleString("tr-TR", {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : null;

const qty = (value) => (value == null ? "—" : formatQuantity(value));

const actorCell = (label, name, at) => ({
  stack: [
    { text: label, fontSize: 8, color: MUTED, characterSpacing: 0.4 },
    {
      text: name ?? "—",
      fontSize: 11,
      bold: true,
      color: name ? INK : MUTED,
      margin: [0, 3, 0, 0],
    },
    { text: stamp(at) ?? " ", fontSize: 8, color: MUTED, margin: [0, 2, 0, 0] },
  ],
});

const noteBlock = (label, text) => ({
  margin: [0, 0, 0, 10],
  stack: [
    { text: label, fontSize: 8, color: MUTED, characterSpacing: 0.4 },
    { text, margin: [0, 2, 0, 0] },
  ],
});

export const supplyPdfFileName = (request) =>
  `ikmal-talebi-${request.id}${request.branchName ? `-${slug(request.branchName)}` : ""}.pdf`;

export const buildSupplyPdf = async ({
  brandName,
  request,
  statusLabel,
  items,
  categories,
}) => {
  const pdfMake = await loadPdfMake();
  const itemById = new Map(items.map((item) => [item.id, item]));
  const categoryById = new Map(
    categories.map((category) => [category.id, category.name]),
  );
  const generatedAt = stamp(Date.now());

  const meta = [
    `Şube: ${request.branchName ?? "—"}`,
    `Durum: ${statusLabel}`,
    `Talep tarihi: ${stamp(request.createdAt)}`,
  ];

  const header = [
    { text: "#", style: "th", alignment: "right" },
    { text: "Kalem", style: "th" },
    { text: "Birim", style: "th" },
    { text: "İstenen", style: "th", alignment: "right" },
    { text: "Onaylanan", style: "th", alignment: "right" },
    { text: "Sevk", style: "th", alignment: "right" },
    { text: "Teslim", style: "th", alignment: "right" },
  ];

  const body = request.lines.map((line, index) => {
    const item = itemById.get(line.itemId);
    const category = item?.categoryId
      ? categoryById.get(item.categoryId)
      : null;

    return [
      { text: String(index + 1), color: MUTED, alignment: "right" },
      {
        stack: [
          { text: item?.name ?? `#${line.itemId}`, bold: true },
          ...(category ? [{ text: category, fontSize: 8, color: MUTED }] : []),
        ],
      },
      { text: item?.unit ?? "", color: MUTED },
      { text: qty(line.requestedQty), alignment: "right" },
      { text: qty(line.approvedQty), alignment: "right", bold: true },
      { text: qty(line.shippedQty), alignment: "right" },
      { text: qty(line.receivedQty), alignment: "right" },
    ];
  });

  const doc = {
    pageSize: "A4",
    pageMargins: [40, 48, 40, 52],
    info: {
      title: `İkmal Talebi #${request.id}`,
      author: brandName,
      creator: "Noxus",
    },
    defaultStyle: {
      font: "Roboto",
      fontSize: 9.5,
      color: INK,
      lineHeight: 1.15,
    },
    styles: {
      th: { bold: true, fontSize: 8.5, color: MUTED, characterSpacing: 0.3 },
    },
    footer: (page, pages) => ({
      margin: [40, 16, 40, 0],
      columns: [
        { text: `${brandName} · ${generatedAt}`, fontSize: 8, color: MUTED },
        {
          text: `Sayfa ${page} / ${pages}`,
          fontSize: 8,
          color: MUTED,
          alignment: "right",
        },
      ],
    }),
    content: [
      {
        text: brandName.toLocaleUpperCase("tr"),
        fontSize: 9,
        bold: true,
        color: ACCENT,
        characterSpacing: 1,
      },
      {
        text: `İkmal Talebi #${request.id}`,
        fontSize: 20,
        bold: true,
        margin: [0, 4, 0, 6],
      },
      { text: meta.join("   ·   "), fontSize: 9, color: MUTED },
      {
        margin: [0, 18, 0, 18],
        table: {
          widths: ["*", "*", "*", "*"],
          body: [
            [
              actorCell("TALEP EDEN", request.createdByName, request.createdAt),
              actorCell(
                request.status === "rejected" ? "REDDEDEN" : "ONAYLAYAN",
                request.decidedByName,
                request.decidedAt,
              ),
              actorCell("SEVK EDEN", request.shippedByName, request.shippedAt),
              actorCell(
                "TESLİM ALAN",
                request.receivedByName,
                request.receivedAt,
              ),
            ],
          ],
        },
        layout: {
          hLineWidth: () => 0,
          vLineWidth: (i) => (i > 0 && i < 4 ? 1 : 0),
          vLineColor: () => LINE,
          paddingLeft: (i) => (i === 0 ? 0 : 14),
          paddingTop: () => 2,
          paddingBottom: () => 2,
        },
      },
      ...(request.note ? [noteBlock("ŞUBENİN NOTU", request.note)] : []),
      ...(request.decisionNote
        ? [noteBlock("MERKEZİN YANITI", request.decisionNote)]
        : []),
      {
        margin: [0, request.note || request.decisionNote ? 8 : 0, 0, 0],
        table: {
          headerRows: 1,
          dontBreakRows: true,
          widths: [18, "*", 58, 48, 58, 40, 44],
          body: [header, ...body],
        },
        layout: {
          hLineWidth: (i, node) =>
            i === 1 || i === node.table.body.length ? 1 : 0,
          hLineColor: () => LINE,
          vLineWidth: () => 0,
          fillColor: (i) => (i > 0 && i % 2 === 0 ? ZEBRA : null),
          paddingTop: () => 5,
          paddingBottom: () => 5,
          paddingLeft: (i) => (i === 0 ? 2 : 6),
          paddingRight: (i, node) =>
            i === node.table.widths.length - 1 ? 2 : 6,
        },
      },
      {
        text: `${request.lines.length} kalem`,
        fontSize: 8,
        color: MUTED,
        margin: [0, 6, 0, 0],
      },
    ],
  };

  return pdfMake.createPdf(doc);
};

export const downloadSupplyPdf = async (input) => {
  const pdf = await buildSupplyPdf(input);

  await pdf.download(supplyPdfFileName(input.request));
};
